import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { IconPanorama360Outline } from '@zillow/constellation';
import { Box, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import { useUpdateVideoLink } from '../../../hooks/queries/video';

import PropertiesActions from '../../../actions/PropertiesActions';

import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';

import AddVideoLinkModalTrigger from '../../Modal/AddVideoLinkModalTrigger';

import { StyledContainer, StyledFlex, StyledIconTrashcanOutline } from './StyledComponents';

const VideoFormField = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { enqueueToast } = useToast();

  const { videoUrl, listingAlias, selfServeOnboardingStatus, canUpdateListing, isArchived, isUpdateListingRestricted } =
    useSelector(
      (state) => ({
        canUpdateListing: state.user.features.canUpdateListing,
        isArchived: state.properties.currentProperty.isArchived,
        isUpdateListingRestricted: state.properties.currentProperty.restrictions.isUpdateListingRestricted,
        videoUrl: state.properties.currentProperty.videoUrl,
        listingAlias: state.properties.currentProperty.listingAlias,
        selfServeOnboardingStatus: state.properties.currentProperty.selfServeOnboardingStatus,
      }),
      shallowEqual,
    );

  const { mutate: deleteLink } = useUpdateVideoLink(listingAlias);

  const isDisabled =
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (selfServeOnboardingStatus.isSelfServeOnboardingInProgress && selfServeOnboardingStatus.isUpdateRestricted);

  const handleDeleteUrl = () => {
    deleteLink('', {
      onSuccess: () => {
        dispatch(PropertiesActions.updateCurrentProperty({ videoUrl: '' }));

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      },
      onError: () => enqueueToast(<Toast tone="critical">Error occurred when deleting video link.</Toast>),
    });
  };

  if (videoUrl) {
    return (
      <StyledFlex display="flex" alignItems="center" gap="xs">
        <StyledIconTrashcanOutline
          data-testid="add-video-delete-button"
          size="xs"
          fontColor="blue500"
          onClick={handleDeleteUrl}
        />
        <TextButton asChild>
          <a target="_blank" href={videoUrl} rel="noreferrer">
            {videoUrl}
          </a>
        </TextButton>
      </StyledFlex>
    );
  }
  return (
    <StyledContainer>
      <Box css={{ marginBottom: '400' }}>
        <IconPanorama360Outline size="md" />
      </Box>
      <Paragraph css={{ marginBottom: '400' }}>Add a video link</Paragraph>
      <AddVideoLinkModalTrigger isDisabled={isDisabled} />
    </StyledContainer>
  );
};

export default VideoFormField;
